import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CheckoutButton } from '../components/Pricing/CheckoutButton';
import { SubscriptionSelector } from '../components/Pricing/SubscriptionSelector';
import { Check, Shield, X } from 'lucide-react';

interface PricingState {
  selectedPlan: 'starter' | 'pro' | 'premium' | null;
  includeSubscription: boolean;
}

const REPORT_PRICE = 197;
const SUBSCRIPTION_PRICES = {
  starter: 29,
  pro: 49,
  premium: 79,
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<PricingState>({
    selectedPlan: null,
    includeSubscription: false,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const calculateTotal = (): number => {
    let total = REPORT_PRICE;
    if (pricing.includeSubscription && pricing.selectedPlan) {
      total += SUBSCRIPTION_PRICES[pricing.selectedPlan];
    }
    return total;
  };

  const handleReportOnly = () => {
    setPricing({ selectedPlan: null, includeSubscription: false });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Unlock Your Career Intelligence
          </h1>
          <p className="text-xl text-gray-300">
            Get your personalized Career Intelligence Report and explore ongoing insights
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Pricing Details */}
          <div className="space-y-8">
            {/* Career Intelligence Report */}
            <div className="bg-gradient-to-br from-teal-900/40 to-blue-900/40 border border-teal-500/30 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Career Intelligence Report
                  </h2>
                  <p className="text-gray-300 text-sm">
                    Comprehensive analysis powered by AI coaching
                  </p>
                </div>
                <span className="text-3xl font-bold text-teal-400">${REPORT_PRICE}</span>
              </div>

              <ul className="space-y-3 mb-6">
                {[
                  'Personalized report with 10-persona AI advisory council',
                  'BRAG & DREAM sheet voice-guided interviews',
                  'Career trajectory analysis',
                  'Actionable insights and recommendations',
                  'Digital report with instant access',
                ].map((feature, i) => (
                  <li key={i} className="flex items-center text-gray-300">
                    <Check className="text-teal-400 mr-3 flex-shrink-0" size={20} />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="text-sm text-gray-400 italic">
                This report is yours forever. One-time payment, no recurring charges.
              </div>
            </div>

            {/* Subscription Options */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4">
                Add Monthly Features (Optional)
              </h3>

              <div className="mb-4 bg-slate-800/50 rounded-lg p-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pricing.includeSubscription}
                    onChange={(e) =>
                      setPricing({
                        ...pricing,
                        includeSubscription: e.target.checked,
                        selectedPlan: e.target.checked ? (pricing.selectedPlan || 'pro') : null,
                      })
                    }
                    className="w-5 h-5 text-teal-500 rounded"
                  />
                  <span className="ml-3 text-white font-medium">
                    Add ongoing insights and features
                  </span>
                </label>
              </div>

              {pricing.includeSubscription && (
                <SubscriptionSelector
                  selectedPlan={pricing.selectedPlan}
                  onSelectPlan={(plan) => setPricing({ ...pricing, selectedPlan: plan })}
                  prices={SUBSCRIPTION_PRICES}
                />
              )}
            </div>
          </div>

          {/* Right: Order Summary & Checkout */}
          <div className="sticky top-20 h-fit">
            <div className="bg-white rounded-lg shadow-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h3>

              {/* Line Items */}
              <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
                <div className="flex justify-between text-gray-700">
                  <span>Career Intelligence Report</span>
                  <span className="font-medium">${REPORT_PRICE.toFixed(2)}</span>
                </div>

                {pricing.includeSubscription && pricing.selectedPlan && (
                  <div className="flex justify-between text-gray-700">
                    <span>
                      {pricing.selectedPlan.charAt(0).toUpperCase() +
                        pricing.selectedPlan.slice(1)}{' '}
                      Plan
                      <span className="text-sm text-gray-500"> (per month)</span>
                    </span>
                    <span className="font-medium">
                      ${SUBSCRIPTION_PRICES[pricing.selectedPlan].toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="space-y-2 mb-8">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Tax (calculated at checkout)</span>
                  <span>Included</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-4 border-t border-gray-200">
                  <span>Total today</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>

                {pricing.includeSubscription && pricing.selectedPlan && (
                  <div className="text-sm text-gray-600 mt-4 p-3 bg-blue-50 rounded">
                    Then ${SUBSCRIPTION_PRICES[pricing.selectedPlan].toFixed(2)}/month thereafter
                  </div>
                )}
              </div>

              {/* Checkout Button */}
              <CheckoutButton
                reportPrice={REPORT_PRICE}
                subscriptionPrice={
                  pricing.selectedPlan ? SUBSCRIPTION_PRICES[pricing.selectedPlan] : null
                }
                subscriptionPlan={pricing.selectedPlan}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                user={user}
              />

              {/* Report-Only Link */}
              {pricing.includeSubscription && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleReportOnly}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Just get the report without subscription
                  </button>
                </div>
              )}

              {/* Trust Badges */}
              <div className="mt-8 pt-8 border-t border-gray-200 space-y-3 text-sm text-gray-600">
                <div className="flex items-center">
                  <Shield className="text-teal-500 mr-2" size={16} />
                  Secure payment powered by Stripe
                </div>
                <div className="flex items-center">
                  <Check className="text-teal-500 mr-2" size={16} />
                  Cancel subscription anytime, no penalties
                </div>
                <div className="flex items-center">
                  <Check className="text-teal-500 mr-2" size={16} />
                  30-day satisfaction guarantee
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
